import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { apiJson } from '@/lib/apiClient';
import type { GeofenceShape } from '@raad/geo';
// The list's OWN key, imported rather than re-declared: a second key spelled the
// same way would invalidate nothing and the new template would not appear until
// a reload.
import { templatesQueryKey } from './useZoneLibrary';

/**
 * Publishing a drawn zone template (slice 24e).
 *
 * The body mirrors `CreateDrawnTemplateBodyZ` in
 * `api/src/modules/zones/templateRoutes.ts`. `shape` is the SAME
 * `GeofenceShapeZ` a tenant posts to create their own zone — reused rather than
 * re-declared, so a template can never hold a shape a real fence could not.
 */
export interface PublishTemplateBody {
  name: string;
  city: string | null;
  region: string | null;
  purpose: 'keep_in' | 'no_go' | 'location' | 'poi';
  /** What a COPY starts with. A suggestion — the tenant decides (slice 24c). */
  suggestedAlertRule: 'never' | 'on_enter' | 'on_exit' | 'on_both';
  notes: string | null;
  shape: GeofenceShape;
}

export function usePublishTemplate(): UseMutationResult<
  { id: string; label: string },
  Error,
  PublishTemplateBody
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiJson<{ id: string; label: string }>('/api/admin/zone-templates/drawn', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: templatesQueryKey }),
  });
}
